import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { useUrnResolver } from '../../hooks/useUrnResolver';
import StatusBadge from '../common/StatusBadge';
import type { OrderFailure } from '../../api/orders';

interface FailuresPanelProps {
  failures: OrderFailure[];
  isLoading?: boolean;
}

function timeAgo(iso: string): string {
  if (!iso) return '--';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 0) return 'just now';
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString([], { month: 'short', day: 'numeric' });
}

export default function FailuresPanel({ failures, isLoading }: FailuresPanelProps) {
  const navigate = useNavigate();
  const { resolveProductUrn, resolveLenderUrn } = useUrnResolver();

  if (isLoading) {
    return (
      <div className="card overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 dark:border-gray-800">
          <div className="h-4 w-32 bg-gray-200 dark:bg-gray-700 rounded animate-pulse" />
        </div>
        <div className="p-5 space-y-2">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-10 bg-gray-100 dark:bg-gray-800 rounded animate-pulse" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="card overflow-hidden">
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100 dark:border-gray-800">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Recent Failures</h3>
          {failures.length > 0 && (
            <span className="px-1.5 py-0.5 rounded text-[10px] font-semibold bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">
              {failures.length}
            </span>
          )}
        </div>
        {failures.length > 0 && (
          <button onClick={() => navigate('/orders?status=failed')} className="text-xs font-medium text-red-600 dark:text-red-400 hover:underline flex items-center gap-1">
            View all <ArrowRight className="h-3 w-3" />
          </button>
        )}
      </div>

      {!failures.length ? (
        <div className="py-8 text-center text-xs text-gray-400 dark:text-gray-500">No failures detected</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-gray-100 dark:border-gray-800">
                {['Time', 'Product', 'Lender', 'Status', 'Error'].map((h) => (
                  <th key={h} className="px-4 py-2 text-left text-[10px] font-medium text-gray-400 dark:text-gray-500 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50 dark:divide-gray-800/50">
              {failures.map((f, idx) => {
                const id = f.urn ? (f.urn.split(':').pop() || f.urn) : String(idx);
                return (
                  <tr
                    key={f.urn || idx}
                    onClick={() => navigate(`/orders/${id}`)}
                    className="hover:bg-red-50/40 dark:hover:bg-red-900/10 cursor-pointer transition-colors"
                  >
                    <td className="px-4 py-2.5 whitespace-nowrap text-gray-500 dark:text-gray-400" title={f.createdAt ? new Date(f.createdAt).toLocaleString() : ''}>
                      {timeAgo(f.createdAt)}
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-gray-900 dark:text-gray-200 truncate max-w-[140px] block" title={f.product}>{resolveProductUrn(f.product)}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-gray-600 dark:text-gray-400 truncate max-w-[120px] block" title={f.originator}>{resolveLenderUrn(f.originator)}</span>
                    </td>
                    <td className="px-4 py-2.5"><StatusBadge status={f.status} /></td>
                    <td className="px-4 py-2.5">
                      <span className="text-red-600 dark:text-red-400 truncate max-w-[220px] block" title={f.message}>{f.message || '--'}</span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
